import React, { useEffect, forwardRef } from 'react'
import classNames from 'classnames'
import './button.css'

export interface ButtonProps {
  /**
   * 容器类名
   */
  className?: string;
  /**
   * 按钮颜色
   */
  color?: string;
  /**
   * 禁用
   */
  disabled?: boolean;
  /**
   * 扁平按钮（文字按钮）
   */
  flat?: boolean;
  /**
   * a标签按钮
   */
  href?: string;
  /**
   * 异步加载禁用
   */
  loading?: boolean;
  /**
   * 边框按钮
   */
  outline?: boolean;
  /**
   * 类名前缀
   */
  prefixClass?: string;
  /**
   * 圆角按钮
   */
  rounded?: boolean | string;
  /**
   * 按钮大小
   * @default medium
   */
  size?: 'large' | 'medium' | 'small' | 'custom' | 'icon';
  /**
   * 容器行内样式
   */
  style?: React.CSSProperties;
  /**
   * a标签按钮
   */
  target?: string;
  /**
   * 点击事件
   */
  onClick?: React.MouseEventHandler<HTMLElement>;
  children?: React.ReactNode;
  [buttonProps: string]: any,
}

// eslint-disable-next-line react/display-name
const BaseButton = forwardRef<unknown, ButtonProps>((props, ref) => {
  const {
    loading,
    prefixClass,
    className,
    style = {},
    onClick,
    disabled,
    size,
    outline,
    flat,
    rounded,
    children,
    href,
    color,
    ...buttonProps
  } = props

  const $button = (ref as React.MutableRefObject<
      HTMLAnchorElement | HTMLButtonElement | null
    >) || React.createRef<HTMLElement>()

  useEffect(() => {
    if ($button.current && rounded && typeof rounded !== 'boolean') {
      $button.current.style.setProperty('--radius', rounded)
    }
  }, [])

  if (href) {
    return (
      <a
        href={href}
        ref={$button as React.MutableRefObject<HTMLAnchorElement | null>}
        className={classNames(`${prefixClass}Btn`, className)}
        data-size={size}
        data-color={color}
        data-outline={outline}
        data-flat={flat}
        data-rounded={!!rounded}
        data-loading={loading}
        data-disabled={disabled}
        style={style}
        onClick={(ev) => {
          if (loading || disabled) {
            ev.preventDefault()
            return
          }
          onClick?.(ev)
        }}
        {...buttonProps}
      >
        {children}
      </a>
    )
  }

  return (
    <button
      type="button"
      ref={$button as React.MutableRefObject<HTMLButtonElement | null>}
      disabled={disabled}
      className={classNames(`${prefixClass}Btn`, className)}
      data-size={size}
      data-color={color}
      data-outline={outline}
      data-flat={flat}
      data-rounded={!!rounded}
      data-loading={loading}
      style={style}
      onClick={(ev) => {
        if (loading || disabled) {
          return
        }
        onClick?.(ev)
      }}
      {...buttonProps}
    >
      {children}
    </button>
  )
})

BaseButton.defaultProps = {
  prefixClass: 'zy',
  size: 'medium',
  color: 'primary',
}

export default BaseButton
